import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, FormControl, IconButton, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";

export interface XeroProductModel {
    id: string;
    name: string;
    code: string;
}

interface AdminGetXeroProductsResponse {
    data: XeroProductsData;
    error: IError;
}
interface XeroProductsData {
    products: XeroProductModel[];
}

interface AdminGetXeroAccountsResponse {
    data: XeroAccountsData;
    error: IError;
}
interface XeroAccountsData {
    accounts: XeroAccountModel[];
}

export interface XeroAccountModel {
    code: string;
    name: string;
}

interface CreateBillingProductRequest {
    productCode: string;
    description: string;
    dbLookup: boolean;
    boothIdFilter: boolean;
    billingType: string;
    unitPrice: number;
    taxRate: boolean;
    allowFree: boolean;
    allowCommission: boolean;
    allowHide: boolean;
    xeroProduct: string;
    xeroAccount: string;
}

interface CreateBillingProductResponse {
    data: CreateXeroProductsData;
    error: IError;
}
interface CreateXeroProductsData {
    status: string;
}


const BillingPerOptions: string[] = ["/month", "/vehicle", "/image"];

function CreateBillingProduct() {
    const { enqueueSnackbar } = useSnackbar();

    const [productCode, setProductCode] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [dbLookup, setDbLookup] = useState<boolean>(false);
    const [boothIdFilter, setBoothIdFilter] = useState<boolean>(false);
    const [billingType, setBillingType] = useState<string>("/month");
    const [unitPrice, setUnitPrice] = useState<number>(0.0);
    const [taxRate, setTaxRate] = useState<boolean>(true);
    const [allowFree, setAllowFree] = useState<boolean>(false);
    const [allowCommission, setAllowCommission] = useState<boolean>(false);
    const [allowHide, setAllowHide] = useState<boolean>(true);

    const [selectedXeroProduct, setSelectedXeroProduct] = useState<string>("");
    const [selectedXeroAccount, setSelectedXeroAccount] = useState<string>("");

    const [xeroProducts, setXeroProducts] = useState<XeroProductModel[]>([]);
    const [xeroAccounts, setXeroAccounts] = useState<XeroAccountModel[]>([]);

    useEffect(() => {
        GetXeroProducts();
        GetXeroAccounts();
    }, []);

    const GetXeroProducts = async () => {
        try {
            const response: AdminGetXeroProductsResponse = await api.get("api/Xero/GetProducts", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setXeroProducts(response.data.products)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetXeroAccounts = async () => {
        try {
            const response: AdminGetXeroAccountsResponse = await api.get("api/Xero/GetAccounts", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setXeroAccounts(response.data.accounts);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const CreateProduct = async () => {
        if (productCode === "" || description === "" || unitPrice === 0 || selectedXeroProduct === "" || selectedXeroAccount === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        const body: CreateBillingProductRequest = {
            productCode: productCode,
            description: description,
            dbLookup: dbLookup,
            boothIdFilter: boothIdFilter,
            billingType: billingType,
            unitPrice: unitPrice,
            taxRate: taxRate,
            allowFree: allowFree,
            allowCommission: allowCommission,
            allowHide: allowHide,
            xeroProduct: selectedXeroProduct,
            xeroAccount: selectedXeroAccount
        };

        try {
            const response: CreateBillingProductResponse = await api.post("api/Admin/CreateBillingProduct", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Created!", { variant: "success" })
                // clear fields
                setProductCode("");
                setDescription("");
                setDbLookup(false);
                setBoothIdFilter(false);
                setBillingType("/month");
                setUnitPrice(0);
                setTaxRate(true);
                setAllowFree(false);
                setAllowCommission(false);
                setAllowHide(true);

                setSelectedXeroProduct("");
                setSelectedXeroAccount("");
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    return (
        <Stack>
            {/* ProductCode */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>ProductCode: </p>
                <TextField
                    value={productCode}
                    onChange={(newValue) => { setProductCode(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Description */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Description: </p>
                <TextField
                    value={description}
                    onChange={(newValue) => { setDescription(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Xero Products */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Xero Product: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedXeroProduct}
                        onChange={(newValue: any) => { setSelectedXeroProduct(newValue.target.value); }}
                        size="small">
                        {xeroProducts.map((item, index) =>
                            <MenuItem
                                key={index}
                                value={item.id}>
                                {item.code} — {item.name}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton
                    color="primary"
                    size="large"
                    onClick={() => { GetXeroProducts() }}>
                    <RefreshIcon sx={{ fontSize: 26 }} />
                </IconButton>
            </Stack>

            {/* Xero Account */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Xero Account: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedXeroAccount}
                        onChange={(newValue: any) => { setSelectedXeroAccount(newValue.target.value); }}
                        size="small">
                        {xeroAccounts.map((item, index) =>
                            <MenuItem
                                key={index}
                                value={item.name}>
                                {item.code} - {item.name}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton
                    color="primary"
                    size="large"
                    onClick={() => { GetXeroAccounts() }}>
                    <RefreshIcon sx={{ fontSize: 26 }} />
                </IconButton>
            </Stack>

            {/* Database lookup */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Database lookup: </p>
                <Switch
                    checked={dbLookup}
                    onChange={(newValue) => { setDbLookup(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* BoothId Filter */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>BoothId Filter: </p>
                <Switch
                    checked={boothIdFilter}
                    onChange={(newValue) => { setBoothIdFilter(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Unit Price */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Unit Price: </p>
                <TextField
                    value={unitPrice}
                    onChange={(newValue: any) => { setUnitPrice(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Billing per */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Billing type: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={billingType}
                        onChange={(newValue: any) => { setBillingType(newValue.target.value); }}
                        size="small">
                        {BillingPerOptions.map((item, index) =>
                            <MenuItem
                                key={index}
                                value={item}>
                                {item}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Tax Rate */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Tax Rate: </p>
                <Switch
                    checked={taxRate}
                    onChange={(newValue) => { setTaxRate(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Allow Free */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Allow Free: </p>
                <Switch
                    checked={allowFree}
                    onChange={(newValue) => { setAllowFree(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Allow Commission */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Allow Commission: </p>
                <Switch
                    checked={allowCommission}
                    onChange={(newValue) => { setAllowCommission(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Allow Hide */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Allow Hide: </p>
                <Switch
                    checked={allowHide}
                    onChange={(newValue) => { setAllowHide(newValue.target.checked) }}
                    size="small" />
            </Stack>

            <Button
                variant="outlined"
                onClick={() => CreateProduct()}
                sx={{ width: '82px' }}>
                Create
            </Button>
        </Stack>
    )
}

export default CreateBillingProduct;